// One day of the trip as a child sees it (mockup 02, the Trip screen's "Today" view): legs and
// plans in time order, each with its tag. Reads the already-masked pack (domain/trip/visible),
// so a surprise still under wraps arrives here as its teaser and gets the 🎁 tag.
import type { LegKind, Pack } from '../../../shared/pack/schema'
import { legBooked } from '../../../shared/trip'
import { instantOf, zoned } from '../time'
import { dateZone, tripZone } from '../../../shared/tripZone'
import { revealed, type TripClock } from './visible'

export type PlanTag = 'booked' | 'want' | 'surprise' | null

export interface PlanRow {
  id: string
  kind: 'leg' | 'item'
  legKind?: LegKind
  /** Wall-clock times as written ("08:35"); none for an untimed plan. */
  time?: string
  endTime?: string
  title: string
  sub?: string
  tag: PlanTag
  placeId?: string
  /** The first row still to come today. */
  next: boolean
  startMs: number
}

/** Rows for `date`, oldest first. `wanted` holds the child's want-to-go place ids. */
export function dayPlan(pack: Pick<Pack, 'meta' | 'itinerary'>, date: string, clock: TripClock & { nowMs: number }, wanted: ReadonlySet<string> = new Set()): PlanRow[] {
  // B13: a leg's day is the day where the trip is when it leaves; a plan's time is the wall clock
  // where its day's stage is.
  const tz = dateZone(pack, date)
  const rows: PlanRow[] = []
  for (const l of pack.itinerary.legs) {
    const startMs = Date.parse(l.departsAt)
    if (zoned(startMs, tripZone(pack, startMs)).date !== date) continue
    rows.push({
      id: `leg:${l.id}`,
      kind: 'leg',
      legKind: l.kind,
      // An untimed journey (WI-42: times to come) shows no clock.
      ...(l.untimed ? {} : { time: l.departsAt.slice(11, 16), endTime: l.arrivesAt.slice(11, 16) }),
      title: `${l.from.name} → ${l.to.name}`,
      ...(l.ref ? { sub: l.ref } : {}),
      tag: legBooked(l) ? 'booked' : null,
      next: false,
      startMs,
    })
  }
  for (const i of pack.itinerary.dayItems) {
    if (i.date !== date) continue
    const wrapped = !!i.surprise && !revealed(i.surprise.until, clock)
    rows.push({
      id: `item:${i.id}`,
      kind: 'item',
      ...(i.time ? { time: i.time } : {}),
      ...(i.endTime ? { endTime: i.endTime } : {}),
      title: i.title,
      ...(i.sub && !wrapped ? { sub: i.sub } : {}),
      tag: wrapped ? 'surprise' : i.booked ? 'booked' : i.placeId && wanted.has(i.placeId) ? 'want' : null,
      ...(i.placeId && !wrapped ? { placeId: i.placeId } : {}),
      next: false,
      // Untimed plans sort to the end of their day.
      startMs: instantOf(i.date, i.time ?? '23:59', tz),
    })
  }
  rows.sort((a, b) => a.startMs - b.startMs)
  if (date === clock.date) {
    const next = rows.find((r) => r.time && r.startMs >= clock.nowMs)
    if (next) next.next = true
  }
  return rows
}
